import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { LandingPage } from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import DemoLoginPage from '@/pages/DemoLoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import { SignupPage } from '@/pages/SignupPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { AssetsPage } from '@/pages/AssetsPage';
import { LocationsPage } from '@/pages/LocationsPage';
import { JHAPage } from '@/pages/JHAPage';
import { CreateJHAPage } from '@/pages/CreateJHAPage';
import { JHADetailPage } from '@/pages/JHADetailPage';
import { EditJHAPage } from '@/pages/EditJHAPage';
import { CertificationsPage } from '@/pages/CertificationsPage';
import { CertShareViewPage } from '@/pages/CertShareViewPage';
import { ConstructionPlansPage } from '@/pages/ConstructionPlansPage';
import { CreateConstructionPlanPage } from '@/pages/CreateConstructionPlanPage';
import { PPEInspectionPage } from '@/pages/PPEInspectionPage';
// RiggingCalculatorPage is intentionally NOT imported here — it's hidden behind the
// Coming Soon tile until vetted by the major tower owners. Re-enable with:
//   import { RiggingCalculatorPage } from '@/pages/RiggingCalculatorPage';
//   <Route path="/rigging-calculator" element={<RiggingCalculatorPage />} />
import { RiggingCalculatorComingSoonPage } from '@/pages/RiggingCalculatorComingSoonPage';
import { SafetyAuditsPage } from '@/pages/SafetyAuditsPage';
import { IncidentHistoryPage } from '@/pages/IncidentHistoryPage';
import { HistoryReportsPage } from '@/pages/HistoryReportsPage';
import { CreateAuditPage } from '@/pages/CreateAuditPage';
import { AuditTemplatesPage } from '@/pages/AuditTemplatesPage';
import { AuditTemplateBuilderPage } from '@/pages/AuditTemplateBuilderPage';
import { AuditAnalyticsPage } from '@/pages/AuditAnalyticsPage';
import { AuditDetailPage } from '@/pages/AuditDetailPage';
import { DotInspectionPage } from '@/pages/DotInspectionPage';
import { WeeklyActivityPage } from '@/pages/WeeklyActivityPage';
import { TeamActivityDashboardPage } from '@/pages/TeamActivityDashboardPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { MyJobsPage } from '@/pages/my-jobs/MyJobsPage';
import { ProjectPacketView } from '@/pages/my-jobs/ProjectPacketView';
import { InitialReportPage } from '@/pages/InitialReportPage';
import { InitialReportsListPage } from '@/pages/InitialReportsListPage';
import { InvestigationWorkspacePage } from '@/pages/InvestigationWorkspacePage';
import { PermissionsMatrixPage } from '@/pages/PermissionsMatrixPage';
import BillingPage from '@/pages/BillingPage';
import OnboardingWelcomePage from '@/pages/OnboardingWelcomePage';
import ProfilePage from '@/pages/ProfilePage';
import AcceptInvitePage from '@/pages/AcceptInvitePage';
import NotificationsPage from '@/pages/NotificationsPage';
import TeamPage from '@/pages/TeamPage';
import { PolicyDocumentsPage } from '@/pages/PolicyDocumentsPage';
import FleetPage from '@/pages/FleetPage';
import { PlanDetailPage } from '@/pages/PlanDetailPage';
import PublicBadgePage from '@/pages/PublicBadgePage';
import type { ReactElement } from 'react';
import { canCreatePlans, canSeeTile, getStoredRole } from '@/utils/roleGate';

/**
 * WD-T4 (B3 residual / R6 / R4): client-side route guard for role-gated pages.
 * The server still enforces every read/write — this guard exists so a worker
 * typing a URL gets the explain-why/how panel instead of a fetch that 403s.
 * Never a silent redirect (Brian's UX standard 2026-09-20).
 */
function GuardRoute({ allowed, title, roles, children }: {
  allowed: boolean;
  title: string;
  roles: string;
  children: ReactElement;
}) {
  if (allowed) return children;
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-lg bg-white rounded-xl shadow p-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{title} — not available for your role</h1>
        <p className="text-gray-600 mb-4">
          {title} is available to {roles} only. If you need access, ask an admin to change your role.
        </p>
        <Link to="/dashboard" className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
            {/* BP6: self-service password recovery */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/demo" element={<DemoLoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/onboarding/welcome" element={<OnboardingWelcomePage />} />
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/locations" element={<LocationsPage />} />
        <Route path="/construction-plans" element={<ConstructionPlansPage />} />
        <Route path="/construction-plans/create" element={
          <GuardRoute allowed={canCreatePlans(getStoredRole())} title="Plan creation" roles="foreman and up">
            <CreateConstructionPlanPage />
          </GuardRoute>
        } />
        <Route path="/construction-plans/:id" element={<PlanDetailPage />} />
        <Route path="/rigging-calculator" element={<RiggingCalculatorComingSoonPage />} />
        <Route path="/safety-audits" element={<SafetyAuditsPage />} />
        <Route path="/history" element={<HistoryReportsPage />} />
        <Route path="/incidents" element={<IncidentHistoryPage />} />
        <Route path="/safety-audits/create" element={<CreateAuditPage />} />
        <Route path="/safety-audits/:id/edit" element={<CreateAuditPage />} />
        <Route path="/safety-audits/templates" element={<AuditTemplatesPage />} />
        <Route path="/safety-audits/templates/create" element={<AuditTemplateBuilderPage />} />
        <Route path="/safety-audits/templates/:id/edit" element={<AuditTemplateBuilderPage />} />
        <Route path="/safety-audits/analytics" element={<AuditAnalyticsPage />} />
        <Route path="/safety-audits/:id" element={<AuditDetailPage />} />
        <Route path="/weekly-activity" element={
          <GuardRoute allowed={canSeeTile(getStoredRole(), 'WEEKLY_ACTIVITY')} title="Weekly Activity" roles="safety manager and admin">
            <WeeklyActivityPage />
          </GuardRoute>
        } />
        <Route path="/team-activity" element={
          <GuardRoute allowed={canSeeTile(getStoredRole(), 'TEAM_DASHBOARD')} title="Team Dashboard" roles="foreman, project manager, safety manager, and admin">
            <TeamActivityDashboardPage />
          </GuardRoute>
        } />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/my-jobs" element={<MyJobsPage />} />
        <Route path="/my-jobs/:id" element={<ProjectPacketView />} />
        <Route path="/jha" element={<JHAPage />} />
        <Route path="/jha/create" element={<CreateJHAPage />} />
        <Route path="/jha/:id" element={<JHADetailPage />} />
        <Route path="/jha/:id/edit" element={<EditJHAPage />} />
        <Route path="/certifications" element={<CertificationsPage />} />
        <Route path="/cert-share/:token" element={<CertShareViewPage />} />
        <Route path="/ppe-inspection" element={<PPEInspectionPage />} />
        <Route path="/dot-inspection" element={<DotInspectionPage />} />
        <Route path="/initial-reports" element={<InitialReportPage />} />
        <Route path="/initial-reports/queue" element={<InitialReportsListPage />} />
        <Route path="/initial-reports/new" element={<InitialReportPage />} />
        <Route path="/investigations/:incidentId" element={<InvestigationWorkspacePage />} />
        <Route path="/permissions" element={<PermissionsMatrixPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/policy-documents" element={<PolicyDocumentsPage />} />
        <Route path="/fleet" element={<FleetPage />} />
        <Route path="/badge/:qrCode" element={<PublicBadgePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;


